// @storybook-internal widget renderer catalogued through PlacedWidgetItem.
import React from "react";
import "./widget-cards.css";

import type {
  T_ChatV8UpcomingEventItem,
  T_ChatV8UpcomingEventsWidgetData,
} from "@lattice/core-lib/types/chat-v8";
import {
  buildGoogleCalendarUrl,
  buildIcsContent,
  getUpcomingEventDateBadge,
  getUpcomingEventStatsLabel,
} from "@lattice/shared-frontend/chat-v8";

export type T_UpcomingEventsCardProps = {
  data: T_ChatV8UpcomingEventsWidgetData;
};

/**
 * Upcoming events card (RAI-1097): web counterpart of the mobile events
 * presentation, with the per-item add-to-calendar affordance the fallback
 * table cannot express. Renders INSTEAD of the table when the artifact
 * carries the upcoming_events presentation; the intro stays in markdown.
 *
 * Calendar handoff uses the shared dependency-free rules: Google opens the
 * render URL template in a new tab; ".ics" downloads a calendar file any
 * calendar app (Apple, Outlook, etc.) can import.
 */
export function UpcomingEventsCard({ data }: T_UpcomingEventsCardProps) {
  const handleDownloadIcs = (item: T_ChatV8UpcomingEventItem) => {
    const ics = buildIcsContent(item.addToCalendar);
    if (ics === null) return;
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `lattice-${item.addToCalendar.sourceEventId.replace(/[^a-zA-Z0-9_-]/g, "-")}.ics`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section aria-label="Upcoming events" className="cw-events">
      <ul className="cw-events__list">
        {data.items.map((item) => {
          const badge = getUpcomingEventDateBadge(item);
          const googleUrl = buildGoogleCalendarUrl(item.addToCalendar);
          return (
            <li
              className="cw-events__item"
              key={item.addToCalendar.sourceEventId}
            >
              {badge !== null ? (
                <span aria-hidden className="cw-events__date">
                  <span className="cw-events__date-month">{badge.month}</span>
                  <span className="cw-events__date-day">{badge.day}</span>
                </span>
              ) : null}
              <div className="cw-events__copy">
                <span className="cw-events__title">{item.title}</span>
                <span className="cw-events__body">{item.body}</span>
                <span className="cw-events__stats">
                  {getUpcomingEventStatsLabel(item.stats)}
                </span>
              </div>
              <div className="cw-events__actions">
                {googleUrl !== null ? (
                  <a
                    className="cw-events__action"
                    href={googleUrl}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Google
                  </a>
                ) : null}
                <button
                  className="cw-events__action"
                  onClick={() => handleDownloadIcs(item)}
                  type="button"
                >
                  Download .ics
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
